import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import CSSModule from 'react-css-modules';
import { ArrowRight, Heart, Search, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Spinner, SeoHead } from 'components';
import { actions as affiliateActions } from 'coupon-common/src/modules/affiliateBusiness';
import {
  actions as affiliateCouponActions,
  duck as affiliateCouponsDuck,
} from 'coupon-common/src/modules/businesses/affiliateCoupons';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { actions as businessCategoriesActions, duck as businessCategoriesDuck } from 'coupon-common/src/modules/businessCategories';
// Separate from businessCategoriesDuck above (that one tags affiliate
// businesses in the admin panel) — this is the customer-facing coupon
// category taxonomy CouponsListAll filters by, the same one Home.js reads
// to build its own "Explore deals by category" tiles/preSelectedCategories
// handoff. Needed here only to resolve "Shopping"'s real id for the
// "See more Offers in Shopping" card's navigation below.
import { actions as couponCategoryActions } from 'coupon-common/src/modules/customerCoupons/categories';
import { COUPON_TYPES } from 'coupon-common';
import { getListFromNormalized } from 'coupon-common/src/utils';
import { SITE_URL } from 'components/SeoHead/SeoHead';

import LoadingLogo from '../../../components/LoadingLogo/LoadingLogo';

// Same fallback Home.js/CouponsMapAll use for coupons with no photo.
// ASSUMPTION: same relative depth as CouponsMapAll's own import of this
// asset — confirm/adjust the leading ../ count if this file's actual
// nesting differs.
import NoCouponImage from '../../../assets/images/NoCouponImage.webp';
import LoginPrompt from 'components/LoginPrompt/LoginPrompt';
import ScrollRail from 'components/ScrollRail/ScrollRail';
import { RouteNames } from '../../../constants';
import styles from './AffiliateBusinessesListAll.module.scss';

// PERF: the list endpoint now returns every business in ONE request
// (?page_size=1000) with `coupon_count` and `best_offer` already attached,
// so the page no longer fires one coupon request per business on load. Full
// coupon objects (images, descriptions, ...) are fetched lazily, only for
// the business whose shelf is open — that's the only place this page still
// uses the affiliateCoupons duck.
const AFFILIATE_COUPON_INITIAL_STATE = affiliateCouponsDuck.AFFILIATE_BUSINESS_COUPONS_INITIAL_STATE;

// Asks the backend for the whole partner list in a single page. The saga
// still follows `next` links, so this degrades gracefully if the server
// caps page_size lower.
const LIST_PARAMS = { page_size: 1000 };

// Same logic as CouponsMapAll's getCouponDealAmount, so a coupon shows the
// identical "$5 off" / "20% off" / "150 points" subtitle everywhere. Works
// on both full coupons and the list endpoint's slim `best_offer`.
const getCouponDealAmount = coupon => {
  if (coupon?.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} off`;
  if (coupon?.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% off`;
  if (coupon?.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} points`;
  return null;
};

// ASSUMPTION: FullCouponListSerializer's inherited (BaseCouponListSerializer)
// fields include `image_url` as an array of { image_url } objects, same
// shape CouponsMapAll assumes for regular business coupons — adjust if the
// affiliate coupon payload's photo field is shaped differently.
const getCouponImage = coupon => {
  const images = coupon?.image_url;
  return Array.isArray(images) && images.length > 0 ? images[0].image_url : NoCouponImage;
};

// A business's `categories` field comes back as an array of {id, name}
// objects (see AffiliateBusinessModal's submit handler / BusinessesDetails'
// `categories.map(cat => cat.name || cat)` usage) — normalize defensively
// in case a single `category` object/id sneaks through instead.
const getBusinessCategoryIds = business => {
  const cats = business?.categories;
  if (Array.isArray(cats)) return cats.map(c => (c && typeof c === 'object' ? c.id : c)).filter(Boolean);
  if (business?.category) return [typeof business.category === 'object' ? business.category.id : business.category];
  return [];
};

const getBusinessImage = business => business?.cover_image || business?.logo || null;

// From the list endpoint (see AffiliateBusinessListSerializer).
const getCouponCount = business => business?.coupon_count || 0;

// Same "largest raw value for its type" score the backend uses to pick
// best_offer. Not truly comparable across types, but there's no backend
// "best offer" flag — used to rank businesses against each other in the
// Shopping row.
const getBestOfferScore = business => {
  const offer = business?.best_offer;
  if (!offer) return 0;
  if (offer.type === COUPON_TYPES.AMOUNT_OFF) return Number(offer.amount_off) || 0;
  if (offer.type === COUPON_TYPES.PERCENTAGE_OFF) return Number(offer.percentage_off) || 0;
  if (offer.type === COUPON_TYPES.POINTS_BACK) return Number(offer.points_back) || 0;
  return 0;
};

// Only these category rows should render on the page right now — matched
// case-insensitively against the category name since ids aren't known
// ahead of time. Update this list if the backend category gets renamed, or
// to show more categories later.
const VISIBLE_CATEGORY_NAMES = ['shopping'];

// "All Brands" pagination page size — see visibleCount in the component.
const ALL_BRANDS_PAGE_SIZE = 10;

// Category rows (currently just "Shopping") cap at this many tiles before
// a trailing "See more Offers in ..." tile takes over — see categoryRows
// rendering below.
const CATEGORY_TILES_LIMIT = 10;

// Per request, "Top Brands" is this fixed, ordered set of partners rather
// than a "most coupons" heuristic — matched case-insensitively against
// each business's real (often longer, e.g. "Dyson Canada Limited") name via
// `includes` so a short label here still finds it. Update this list to
// change which brands show there.
const TOP_BRANDS_NAMES = [
  'Dyson',
  'Well.ca',
  'Brother',
  'Aosom',
  'Samsung',
  'Expedia',
  'Under Armour',
  'Emma Sleep',
  'Temu',
  'City Pass',
];

// ─── "Discover Deals That Matter" tiles ──────────────────────────────────
// Per request, this row is a fixed set of 6 marketing/design categories —
// NOT driven by real categoryRows data, and shown even when a category has
// no affiliate businesses (or doesn't exist yet) behind it. Every one of
// these is treated as a SHOPPING subcategory (Men's Apparels, Electronics,
// ...) rather than its own main category — clicking a tile always hands
// CouponsListAll "Shopping" as preSelectedCategories, same as the "See more
// Offers in Shopping" card, and additionally tries to resolve `matchNames`
// against Shopping's own real subcategories for preSelectedSubCategories.
// If none of a tile's names match a real subcategory (the backend may not
// have that exact subcategory yet), the tile still lands on "Shopping
// Coupons" with no subcategory filter, rather than throwing.
//
// Image paths point at /images/discover/<slug>.webp, following the same
// public-folder convention this file already uses for HeroSectionBg.webp —
// drop the provided photos in at those paths (public/images/discover/).
const DISCOVER_TILES = [
  {
    key: 'mens-apparel',
    title: "Men's Apparels",
    image: '/images/discover/mens-apparel.webp',
    matchNames: ["men's apparel", 'mens apparel', "men's apparels", "men's fashion", 'mens fashion'],
  },
  {
    key: 'electronics',
    title: 'Electronics',
    image: '/images/discover/electronics.webp',
    matchNames: ['electronics'],
  },
  {
    key: 'cosmetics',
    title: 'Cosmetics',
    image: '/images/discover/cosmetics.webp',
    matchNames: ['cosmetics', 'beauty'],
  },
  {
    key: 'womens-apparel',
    title: "Women's Apparel",
    image: '/images/discover/womens-apparel.webp',
    matchNames: ["women's apparel", 'womens apparel', "women's apparels", "women's fashion", 'womens fashion'],
  },
  {
    key: 'everyday-essentials',
    title: 'Every day Essentials',
    image: '/images/discover/everyday-essentials.webp',
    matchNames: ['everyday essentials', 'every day essentials', 'essentials'],
  },
  {
    key: 'home-decors',
    title: 'Home Decors',
    image: '/images/discover/home-decors.webp',
    matchNames: ['home decor', 'home decors', 'home & decor', 'home'],
  },
];

const AffiliateBusinessesListAll = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const affiliateState = useSelector(state => state.affiliateBusiness) || {};
  const { fetching, fetched, data, error } = affiliateState;
    const list = useMemo(() => (data && data.results) || [], [data]);

  // Keyed by business id — mirrors CouponsMapAll's businesses.coupons
  // pattern: state.business.affiliateCoupons[id] = {fetching, fetched, data, error}.
  // Now only populated for businesses whose shelf has been opened.
  const rawAffiliateCouponsState = useSelector(state => state.businesses.affiliateCoupons);
  const affiliateCouponsState = useMemo(() => rawAffiliateCouponsState || {}, [rawAffiliateCouponsState]);

  // Same category picker data BusinessCategoriesField uses for the admin
  // form — reused here (read-only) to group affiliate businesses into
  // "Men's apparel"-style rows instead of hardcoding category content.
  const businessCategoriesState = useSelector(state => state.businessCategories);
  const categoryList = useMemo(
    () => getListFromNormalized(businessCategoriesState, businessCategoriesDuck.businessCategoriesEntity.key),
    [businessCategoriesState],
  );

  // Customer-facing coupon categories (CouponsListAll's own taxonomy — see
  // the import note above), fetched only to resolve "Shopping"'s real id
  // (and its real subcategories) for the "See more Offers in Shopping" card
  // and the Discover Deals tiles' preSelectedCategories/preSelectedSubCategories
  // handoff below.
  const couponCategoryState = useSelector(state => state.customerCoupons?.categories) || {};
  const couponCategories = useMemo(
    () => (couponCategoryState.data && couponCategoryState.data.results) || couponCategoryState.data || [],
    [couponCategoryState.data],
  );
  const shoppingCouponCategory = useMemo(
    () => couponCategories.find(c => (c.name || '').trim().toLowerCase() === 'shopping') || null,
    [couponCategories],
  );
  const shoppingCouponCategoryId = shoppingCouponCategory?.id ?? null;

  // Best-effort subcategory lookup for a Discover Deals tile — e.g. "Men's
  // Apparels" resolves against Shopping's own subcategories list for a
  // "Men's Apparel"-ish name. Returns null (category-only filter) when
  // Shopping has no matching subcategory, same "don't throw, just filter
  // less precisely" fallback.
  const findShoppingSubCategoryIdByNames = useCallback(
    matchNames => {
      const subcategories = shoppingCouponCategory?.subcategories || [];
      for (const name of matchNames) {
        const found = subcategories.find(sub => (sub.name || '').trim().toLowerCase() === name);
        if (found) return found.id;
      }
      return null;
    },
    [shoppingCouponCategory],
  );

  // Same follow duck BusinessesDetails.js / AffiliateBusinessDetails.js use
  // — "Favorite Brands" is the user's own followed-business list, not a
  // field on the business itself.
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));
  const followState = useSelector(state => state.businesses.follow);
  const followedIds = useMemo(
    () => new Set((followState?.data?.results || []).map(r => r.business_id)),
    [followState?.data?.results],
  );
  const getFollowRecord = useCallback(
    id => (followState?.data?.results || []).find(r => r.business_id === id),
    [followState?.data?.results],
  );
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  // The overlay now only waits on the business list itself (which already
  // carries coupon_count / best_offer) — no per-business coupon fetches.
  const businessListSettled = fetched || !!error;
  const showLoadingOverlay = !businessListSettled;

  const [searchKey, setSearchKey] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(null);

  // Hero search-bar typeahead — "starts with" (not the "includes" match the
  // "All Brands" grid below uses) so typing "D" surfaces stores named
  // Dyson/Dyson Canada Limited first, matching a normal autocomplete feel.
  // Capped at 8 so the dropdown never grows past a reasonable list.
  const searchSuggestions = useMemo(() => {
    const key = searchKey.trim().toLowerCase();
    if (!key) return [];
    return list.filter(b => b.name?.toLowerCase().startsWith(key)).slice(0, 8);
  }, [list, searchKey]);

  // "All Brands" pagination — show ALL_BRANDS_PAGE_SIZE at a time, "More"
  // reveals another page.
  const [visibleCount, setVisibleCount] = useState(ALL_BRANDS_PAGE_SIZE);

  // Absolutely-positioned overlay instead of a grid-flow sibling — this is
  // what keeps every other card exactly where it is when a business gets
  // selected. gridRef anchors the positioning; cardRefs lets us measure
  // exactly where the selected card's bottom edge is so the shelf sits
  // right under it, "coming forward" over the row(s) below instead of
  // pushing them down.
  const gridRef = useRef(null);
  const cardRefs = useRef({});
  const [shelfTop, setShelfTop] = useState(0);

  // Horizontal rails (Top Brands, Favorite Brands, category rows) use the
  // shared ScrollRail component, which owns its own arrows + scroll state.

  useEffect(() => {
    dispatch(affiliateActions.getAffiliateBusinesses(LIST_PARAMS));
    dispatch(businessCategoriesActions.getBusinessCategories());
    dispatch(followActions.getFollowedBusinessesRequest());
    dispatch(couponCategoryActions.getCategoryList());
  }, [dispatch]);

  const goToDetails = useCallback(id => navigate(`${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${id}`), [navigate]);

  // Full coupons for ONE business (the open shelf) — still read from the
  // affiliateCoupons duck, same shape as before.
  const getBusinessCoupons = useCallback(
    businessId => {
      const entry = affiliateCouponsState[businessId] || AFFILIATE_COUPON_INITIAL_STATE;
      const raw = entry.data;
      // ASSUMPTION: AffiliateOrFlyerCouponsView returns a bare array
      // (serializer.data straight off get_serializer(queryset, many=True))
      // — fall back to {coupons:[...]} / {results:[...]} in case that changes.
      const coupons = Array.isArray(raw) ? raw : raw?.coupons || raw?.results || [];
      return { ...entry, coupons };
    },
    [affiliateCouponsState],
  );

  // Lazy-load the open shelf's coupons. Covers every way a business can get
  // selected (card click, or a tile that sets selectedId directly). Same
  // fetched/fetching guard the old fetch-everything effect used.
  useEffect(() => {
    if (!selectedId) return;
    const entry = affiliateCouponsState[selectedId] || AFFILIATE_COUPON_INITIAL_STATE;
    if (entry.fetched || entry.fetching) return;
    dispatch(affiliateCouponActions.getDetail(selectedId));
    // affiliateCouponsState intentionally omitted — the guard above already
    // prevents duplicate dispatches, and this should only re-run when the
    // selection changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, dispatch]);

  const selectBusiness = useCallback(id => {
    setSelectedId(prev => (prev === id ? null : id));
  }, []);

  const openCoupon = coupon => {
    const target = coupon?.affiliate_link || coupon?.business_web_url;
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  // Clicking a Shopping-row tile should go straight to the deal (its
  // affiliate link / the business's own site) instead of selecting the
  // card in the "All Brands" grid below. Uses the list endpoint's
  // best_offer, so no coupon fetch is needed.
  const openBusinessBestOffer = business => {
    const target = business?.best_offer?.affiliate_link || business?.business_web_url;
    if (!target) return;
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  const toggleFollow = useCallback(
    (business, e) => {
      e?.stopPropagation();
      if (!authorized) {
        setLoginPromptVisible(true);
        return;
      }
      const record = getFollowRecord(business.id);
      if (record) {
        dispatch(
          followActions.unfollowBusinessRequest({
            id: record.id,
            business_id: business.id,
            is_affiliate_or_flyer: true,
          }),
        );
      } else {
        dispatch(
          followActions.followBusinessRequest({
            business_id: business.id,
            is_affiliate_or_flyer: true,
          }),
        );
      }
    },
    [authorized, dispatch, getFollowRecord],
  );

  const filteredBusinesses = useMemo(() => {
    let result = list;
    if (activeCategoryId) {
      result = result.filter(business => getBusinessCategoryIds(business).includes(activeCategoryId));
    }
    if (searchKey.trim()) {
      const lowerSearchKey = searchKey.toLowerCase();
      result = result.filter(business => business.name?.toLowerCase().includes(lowerSearchKey));
    }
    // Two-tier stable sort: any business with at least one coupon stays
    // ahead of every business with none, original list order kept within
    // each tier. coupon_count now arrives with the list, so this no longer
    // reshuffles as per-business fetches resolve.
    return [...result].sort((a, b) => {
      const aHasCoupons = getCouponCount(a) > 0;
      const bHasCoupons = getCouponCount(b) > 0;
      if (aHasCoupons === bHasCoupons) return 0;
      return aHasCoupons ? -1 : 1;
    });
  }, [list, searchKey, activeCategoryId]);

  const selectedBusiness = useMemo(
    () => filteredBusinesses.find(b => b.id === selectedId) || null,
    [filteredBusinesses, selectedId],
  );
  const selectedCoupons = selectedBusiness ? getBusinessCoupons(selectedBusiness.id) : null;

  // Reset "All Brands" back to the first page whenever the underlying
  // filter changes (new search, category tile clicked) — otherwise a
  // narrower result set could leave visibleCount stuck past the end.
  useEffect(() => {
    setVisibleCount(ALL_BRANDS_PAGE_SIZE);
  }, [searchKey, activeCategoryId]);

  // If a business gets selected from somewhere that bypasses the visible
  // page (e.g. a category/discover tile), make sure its card is actually
  // showing rather than opening a shelf under a card that isn't rendered.
  useEffect(() => {
    if (!selectedId) return;
    const idx = filteredBusinesses.findIndex(b => b.id === selectedId);
    if (idx === -1) return;
    setVisibleCount(prev => (idx < prev ? prev : idx + 1));
  }, [selectedId, filteredBusinesses]);

  const visibleBusinesses = useMemo(
    () => filteredBusinesses.slice(0, visibleCount),
    [filteredBusinesses, visibleCount],
  );

  // ─── Top Brands — the fixed TOP_BRANDS_NAMES set above, in that order,
  // resolved against the real affiliate list (skipping any not present yet).
  const topBrands = useMemo(() => {
    return TOP_BRANDS_NAMES.map(name =>
      list.find(b => b.name?.toLowerCase().includes(name.toLowerCase())),
    ).filter(Boolean);
  }, [list]);

  // ─── Favorite Brands — the current user's own followed businesses,
  // cross-referenced against this affiliate list (favorites live on the
  // follow duck, not on the business object itself).
  // Real follows first; when the viewer isn't logged in (or hasn't followed
  // anyone yet) fall back to a showcase of real partners so this section
  // still renders as the page's third section instead of disappearing.
  const favoriteBrands = useMemo(() => {
    const followedList = list.filter(b => followedIds.has(b.id));
    if (followedList.length > 0) return followedList;
    return list.filter(b => b.logo).slice(0, 12);
  }, [list, followedIds]);

  // ─── Category rows — group affiliate businesses by their real
  // categories (fetched from businessCategories, the same module the admin
  // AffiliateBusinessModal uses to tag a business), filtered down to
  // VISIBLE_CATEGORY_NAMES above.
  const categoryRows = useMemo(() => {
    return categoryList
      .filter(category => VISIBLE_CATEGORY_NAMES.includes((category.name || '').trim().toLowerCase()))
      .map(category => ({
        ...category,
        businesses: list.filter(b => getBusinessCategoryIds(b).includes(category.id) && getBusinessImage(b)),
      }))
      .filter(category => category.businesses.length > 0);
  }, [categoryList, list]);

  const measureShelfPosition = useCallback(() => {
    if (!selectedId || !gridRef.current) return;
    const card = cardRefs.current[selectedId];
    if (!card) return;
    const gridRect = gridRef.current.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    setShelfTop(cardRect.bottom - gridRect.top + 16);
  }, [selectedId]);

  useLayoutEffect(() => {
    measureShelfPosition();
  }, [measureShelfPosition]);

  // Grid columns reflow at different viewport widths (auto-fill), which can
  // move the selected card to a different row — recompute on resize so the
  // overlay doesn't end up floating over the wrong row.
  useEffect(() => {
    window.addEventListener('resize', measureShelfPosition);
    return () => window.removeEventListener('resize', measureShelfPosition);
  }, [measureShelfPosition]);

  const getInitials = name => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
    return initials.toUpperCase();
  };

  // Deterministic accent color per business, used as the fallback badge
  // when a business has no image.
  const badgeVariant = name => {
    if (!name) return 0;
    let hash = 0;
    for (let i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % 5;
  };

  // ─── SEO ────────────────────────────────────────────────────────────
  const pagePath = RouteNames.AFFILIATE_BUSINESSES_LIST_ALL;
  const seoTitle = "Top Online Promo Codes & Brands | My Couponbook";
  const seoDescription = "Search verified promo codes and discounts from top online brands across Canada. Find the best online deals and start saving money today.";

  const structuredData =
    list && list.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: list.slice(0, 50).map((business, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: business.name,
            url: `${SITE_URL}${RouteNames.AFFILIATE_BUSINESS_DETAILS}/${business.id}`,
          })),
        }
      : undefined;

  return (
    <div styleName="page">
      {showLoadingOverlay && (
        <div styleName="loading-overlay">
          <LoadingLogo size={96} label="Loading partner deals…" />
        </div>
      )}
      <SeoHead title={seoTitle} description={seoDescription} path={pagePath} type="website" structuredData={structuredData} />

      {/* Same hero mechanism as CouponsListAll: full-bleed background image
          + dark overlay + white heading/subtitle + a glass-pill search bar,
          just with this page's own copy and no city photo dependency. The
          site-wide nav (CustomerHeader, rendered by CustomerLayout) already
          sits above this — this hero is not a page-local nav.
          Background is the provided brand-wall image (static design asset,
          not built from real partner logos anymore) — drop it in at
          public/images/HeroSectionBg.webp. */}
      <div styleName="hero">
        <img src="/images/HeroSectionBg.webp" alt="" styleName="hero-bg" />
        <div styleName="hero-overlay" />

        <div styleName="hero-inner">
          <p styleName="eyebrow">Partner Deals</p>
          <h1 styleName="hero-title">Deals from brands you love</h1>
          <p styleName="hero-subtitle">
            Discover the best deals and promo codes from hundreds of online brands. Find savings on clothing, electronics, travel, beauty, home & garden, food, and more.
          </p>

          <div styleName="search-bar-wrap">
            <Search size={18} styleName="search-icon" />
            <input
              type="text"
              placeholder="What brand are you looking for?"
              value={searchKey}
              onChange={e => setSearchKey(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
              styleName="search-input"
            />
            {searchKey.length > 0 && (
              <button type="button" styleName="clear-btn" onClick={() => setSearchKey('')} aria-label="Clear search">
                <X size={16} />
              </button>
            )}

            {searchFocused && searchKey.trim().length > 0 && (
              <div styleName="search-suggestions">
                {searchSuggestions.length > 0 ? (
                  searchSuggestions.map(business => (
                    <button
                      type="button"
                      key={business.id}
                      styleName="search-suggestion-item"
                      onMouseDown={() => {
                        setSearchFocused(false);
                        goToDetails(business.id);
                      }}
                    >
                      {business.logo ? (
                        <img src={business.logo} alt="" styleName="search-suggestion-logo" />
                      ) : (
                        <span styleName={`badge badge-${badgeVariant(business.name)}`}>{getInitials(business.name)}</span>
                      )}
                      <span styleName="search-suggestion-name">{business.name}</span>
                    </button>
                  ))
                ) : (
                  <p styleName="search-suggestion-empty">No stores match &quot;{searchKey}&quot;.</p>
                )}
              </div>
            )}
          </div>

          <p styleName="disclosure">
            My Couponbook may receive payment for purchases made through links to these partner stores.
          </p>
        </div>
      </div>

      <div styleName="wrap">
        {!error && topBrands.length > 0 && (
          <section styleName="section">
            <div styleName="section-header">
              <span styleName="section-dot" />
              <h2 styleName="section-title">Top Brands</h2>
            </div>
            <ScrollRail className={styles["top-brands-row"]} ariaLabel="top brands">
              {topBrands.map(business => {
                const followed = followedIds.has(business.id);
                const categoryNames = (business.categories || []).map(c => c.name || c).filter(Boolean);
                return (
                  <div
                    key={business.id}
                    role="button"
                    tabIndex={0}
                    styleName="top-brand-card"
                    onClick={() => goToDetails(business.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') goToDetails(business.id);
                    }}
                  >
                    <span styleName="top-brand-image-wrap">
                      <img src={getBusinessImage(business)} alt="" styleName="top-brand-image" />
                    </span>

                    <div styleName="top-brand-panel">
                      <button
                        type="button"
                        styleName={followed ? 'top-brand-heart top-brand-heart-active' : 'top-brand-heart'}
                        onClick={e => toggleFollow(business, e)}
                        aria-label={followed ? 'Unfollow' : 'Follow'}
                      >
                        <Heart size={16} fill={followed ? 'currentColor' : 'none'} />
                      </button>

                      <span styleName="top-brand-name">{business.name}</span>
                      {categoryNames.length > 0 && (
                        <span styleName="top-brand-desc">{categoryNames.join(', ')}</span>
                      )}

                      <span
                        styleName={followed ? 'top-brand-follow-btn top-brand-follow-btn-active' : 'top-brand-follow-btn'}
                        onClick={e => toggleFollow(business, e)}
                        role="button"
                        tabIndex={-1}
                      >
                        {followed ? 'Following' : 'Follow'}
                      </span>
                    </div>

                    <span styleName="top-brand-logo-wrap">
                      {business.logo ? (
                        <img src={business.logo} alt="" styleName="top-brand-logo" />
                      ) : (
                        <span styleName={`badge badge-${badgeVariant(business.name)}`}>{getInitials(business.name)}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </ScrollRail>
          </section>
        )}

        {favoriteBrands.length > 0 && (
          <section styleName="section">
            <div styleName="section-header">
              <span styleName="section-dot" />
              <h2 styleName="section-title">Favourite Brands</h2>
            </div>
            <ScrollRail className={styles["favorites-rail"]} ariaLabel="favourite brands">
                {favoriteBrands.map(business => (
                  <button
                    type="button"
                    key={business.id}
                    styleName="favorite-chip"
                    onClick={() => goToDetails(business.id)}
                    title={business.name}
                  >
                    {business.logo ? (
                      <img src={business.logo} alt={business.name} styleName="favorite-chip-img" />
                    ) : (
                      <span styleName={`badge badge-${badgeVariant(business.name)}`}>{getInitials(business.name)}</span>
                    )}
                  </button>
                ))}
            </ScrollRail>
          </section>
        )}

        {!error &&
          categoryRows.slice(0, 4).map(category => {
            const isShopping = (category.name || '').trim().toLowerCase() === 'shopping';

            // Rank this category's businesses by their own best offer so
            // the row leads with its strongest deals, then cap it at
            // CATEGORY_TILES_LIMIT — any remainder is what the trailing
            // "See more" tile represents.
            const rankedBusinesses = [...category.businesses].sort(
              (a, b) => getBestOfferScore(b) - getBestOfferScore(a),
            );
            const topBusinesses = rankedBusinesses.slice(0, CATEGORY_TILES_LIMIT);
            const hasMore = rankedBusinesses.length > CATEGORY_TILES_LIMIT;

            return (
              <section styleName="section" key={category.id}>
                <div styleName="section-header">
                  <span styleName="section-dot" />
                  <h2 styleName="section-title">{category.name}</h2>
                </div>
                <ScrollRail className={styles["category-tiles-row"]} ariaLabel={category.name}>
                    {topBusinesses.map(business => {
                      const bestDeal = getCouponDealAmount(business.best_offer);
                      return (
                        <button
                          type="button"
                          key={business.id}
                          styleName="category-tile"
                          onClick={() => openBusinessBestOffer(business)}
                        >
                          <img src={getBusinessImage(business)} alt="" styleName="category-tile-img" />
                          <div styleName="category-tile-overlay" />
                          <span styleName="category-tile-tag">{category.name}</span>
                          <div styleName="category-tile-text">
                            <p styleName="category-tile-title">{bestDeal || 'Store offer'}</p>
                            <p styleName="category-tile-subtitle">Store offer</p>
                          </div>
                        </button>
                      );
                    })}

                    {hasMore && isShopping && (
                      <button
                        type="button"
                        styleName="category-tile category-tile-more"
                        onClick={() =>
                          navigate(RouteNames.COUPONS_LISTS_ALL, {
                            state: shoppingCouponCategoryId ? { preSelectedCategories: [shoppingCouponCategoryId] } : {},
                          })
                        }
                      >
                        See more Offers in Shopping
                      </button>
                    )}
                </ScrollRail>
              </section>
            );
          })}

        <section styleName="section">
          <div styleName="section-header">
            <span styleName="section-dot" />
            <h2 styleName="section-title">All Brands</h2>
            {activeCategoryId && (
              <button type="button" styleName="filter-chip" onClick={() => setActiveCategoryId(null)}>
                {categoryList.find(c => c.id === activeCategoryId)?.name || 'Filtered'}
                <X size={12} />
              </button>
            )}
          </div>

          {error && <p styleName="state-message">Something went wrong loading partner stores.</p>}

          {!error && !fetched && fetching && (
            <div styleName="state-message">
              <Spinner show size="md" />
            </div>
          )}

          {!error && (!list || list.length === 0) && fetched && (
            <p styleName="state-message">No partner stores yet — check back soon.</p>
          )}

          {!error && filteredBusinesses.length === 0 && list.length > 0 && fetched && (
            <p styleName="state-message">No stores match "{searchKey}".</p>
          )}

          {!error && filteredBusinesses.length > 0 && (
            <>
              <div styleName="grid" ref={gridRef}>
              {visibleBusinesses.map(item => {
                const isSelected = item.id === selectedId;
                const couponCount = getCouponCount(item);
                const followed = followedIds.has(item.id);

                return (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    ref={el => (cardRefs.current[item.id] = el)}
                    styleName={isSelected ? 'card card-selected' : 'card'}
                    onClick={() => selectBusiness(item.id)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') selectBusiness(item.id);
                    }}
                  >
                    {couponCount > 0 && (
                      <span styleName="card-coupon-badge">
                        {couponCount} {couponCount === 1 ? 'Offer' : 'Offers'}
                      </span>
                    )}
                    <span styleName="card-logo-wrap">
                      {item.logo ? (
                        <img src={item.logo} alt={item.name} styleName="card-logo" />
                      ) : (
                        <span styleName={`badge badge-${badgeVariant(item.name)}`}>{getInitials(item.name)}</span>
                      )}
                    </span>
                    <span styleName="card-name">{item.name}</span>
                    <span styleName="card-cta">
                      {isSelected ? 'Hide Coupons' : 'View Coupons'}
                      <ArrowRight
                        size={14}
                        styleName={isSelected ? 'card-cta-chevron card-cta-chevron-open' : 'card-cta-chevron'}
                      />
                    </span>

                    <button
                      type="button"
                      styleName={followed ? 'card-follow-btn card-follow-btn-active' : 'card-follow-btn'}
                      onClick={e => toggleFollow(item, e)}
                    >
                      {followed ? 'Following' : 'Follow'}
                    </button>
                  </div>
                );
              })}

              {selectedBusiness && (
                <div styleName="shelf" style={{ top: shelfTop }}>
                  <div styleName="shelf-header">
                    <h3 styleName="shelf-title">{selectedBusiness.name}</h3>
                    <div styleName="shelf-header-actions">
                      <button type="button" styleName="shelf-visit-btn" onClick={() => goToDetails(selectedBusiness.id)}>
                        View business
                      </button>
                      <button
                        type="button"
                        styleName="shelf-close-btn"
                        onClick={() => setSelectedId(null)}
                        aria-label="Close"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  {!selectedCoupons.fetched && !selectedCoupons.error && <Spinner show size="sm" />}

                  {selectedCoupons.error && <p styleName="shelf-state-text">Couldn't load coupons for this store.</p>}

                  {!selectedCoupons.fetching &&
                    !selectedCoupons.error &&
                    selectedCoupons.fetched &&
                    selectedCoupons.coupons.length === 0 && (
                      <p styleName="shelf-state-text">No active coupons for this store right now.</p>
                    )}

                  {selectedCoupons.coupons.length > 0 && (
                    <div styleName="shelf-coupons">
                      {selectedCoupons.coupons.map(coupon => {
                        // ASSUMPTION: no confirmed field name for a
                        // per-coupon category/tag on the affiliate coupon
                        // payload (matches the reference design's
                        // "Resort" / "Cafe" pill) — check a few likely
                        // shapes and simply omit the tag if none are set,
                        // rather than fabricating one.
                        const couponTag = coupon?.tag || coupon?.category?.name || coupon?.offer_category || null;
                        return (
                          <button key={coupon.id} type="button" styleName="coupon-card" onClick={() => openCoupon(coupon)}>
                            <img src={getCouponImage(coupon)} alt="" styleName="coupon-card-img" />
                            <div styleName="coupon-card-overlay" />
                            {couponTag && <span styleName="coupon-card-tag">{couponTag}</span>}
                            <div styleName="coupon-card-text">
                              <p styleName="coupon-card-title">{coupon.title || 'Special Offer'}</p>
                              <p styleName="coupon-card-subtitle">
                                {getCouponDealAmount(coupon) || coupon.description || 'Tap to view details'}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              </div>

              {filteredBusinesses.length > visibleCount && (
                <div styleName="load-more-wrap">
                  <button
                    type="button"
                    styleName="load-more-btn"
                    onClick={() => setVisibleCount(prev => prev + ALL_BRANDS_PAGE_SIZE)}
                  >
                    More
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {!error && (
          <section styleName="section">
            <div styleName="section-header">
              <span styleName="section-dot" />
              <h2 styleName="section-title">Discover Deals That Matter</h2>
            </div>
            <div styleName="discover-grid">
              {DISCOVER_TILES.map(tile => (
                <button
                  type="button"
                  key={tile.key}
                  styleName="discover-tile"
                  onClick={() => {
                    // Every Discover tile is a Shopping subcategory (Men's
                    // Apparels, Electronics, ...), so the main category is
                    // always Shopping — only the subcategory differs per
                    // tile, resolved best-effort against Shopping's real
                    // subcategories; when none matches, this still lands on
                    // "Shopping Coupons" rather than something unfiltered.
                    const subCategoryId = findShoppingSubCategoryIdByNames(tile.matchNames);
                    navigate(RouteNames.COUPONS_LISTS_ALL, {
                      state: {
                        ...(shoppingCouponCategoryId ? { preSelectedCategories: [shoppingCouponCategoryId] } : {}),
                        ...(subCategoryId ? { preSelectedSubCategories: [subCategoryId] } : {}),
                      },
                    });
                  }}
                >
                  <img src={tile.image} alt="" styleName="discover-tile-img" />
                  <div styleName="discover-tile-overlay" />
                  <div styleName="discover-tile-text">
                    <p styleName="discover-tile-title">{tile.title}</p>
                    <span styleName="discover-tile-badge">Now Up To 25% Off</span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <LoginPrompt
        isOpen={loginPromptVisible}
        onClose={() => setLoginPromptVisible(false)}
      />
    </div>
  );
};

export default CSSModule(AffiliateBusinessesListAll, styles, { allowMultiple: true });